import React from "react";
import { Card, Grid, Divider, Stepper, Step, StepLabel, Chip } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import StatusChip from "../components/StatusChip";

const LIFECYCLE_STATES = ["Provisioning", "Active", "Suspended", "Maintenance", "Offboarding", "Archived"];

const PROVISIONING_QUEUE = [
  { tenant: "Pied Piper", step: "Infrastructure allocation", progress: 3, total: 8, started: "10 Jul 2025, 08:30" },
  { tenant: "Umbrella Digital", step: "DNS & routing setup", progress: 6, total: 8, started: "10 Jul 2025, 07:15" },
];

const OFFBOARDING_TRACKER = [
  { tenant: "Hooli TV", stage: "Data export in progress", daysLeft: 22, initiated: "18 Jun 2025" },
];

const RECENT_TRANSITIONS = [
  { tenant: "Initech LLC", from: "Active", to: "Suspended", reason: "Payment failure", time: "8 Jul 2025, 09:00", actor: "system" },
  { tenant: "Stark Streaming", from: "Active", to: "Maintenance", reason: "Scheduled DB migration", time: "7 Jul 2025, 02:00", actor: "devops@vivre.io" },
  { tenant: "Pied Piper", from: null, to: "Provisioning", reason: "New tenant onboarding", time: "10 Jul 2025, 08:30", actor: "admin@vivre.io" },
  { tenant: "Hooli TV", from: "Active", to: "Offboarding", reason: "Customer cancellation", time: "18 Jun 2025, 11:15", actor: "support@vivre.io" },
];

const STATE_COUNTS = { Provisioning: 2, Active: 4, Suspended: 1, Maintenance: 1, Offboarding: 1, Archived: 0 };

function Lifecycle() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3}>
        {/* Page Header */}
        <MDBox mb={2.5}>
          <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
            <span style={{ color: "#5D6D7E", fontSize: 12 }}>Control Plane</span>
            <span style={{ color: "#BDC3C7" }}>›</span>
            <span style={{ color: "#1E3A5F", fontSize: 12, fontWeight: 600 }}>Lifecycle</span>
          </MDBox>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1E3A5F" }}>Tenant Lifecycle</h1>
        </MDBox>

        {/* State Machine Visualizer */}
        <Card sx={{ border: "1px solid #E8EDF2", mb: 3 }}>
          <MDBox px={2.5} py={2}>
            <MDTypography variant="h6" fontWeight="bold" color="dark">State Distribution</MDTypography>
          </MDBox>
          <Divider sx={{ m: 0 }} />
          <MDBox px={2.5} py={2.5}>
            <MDBox display="flex" gap={2} flexWrap="wrap" mb={2}>
              {LIFECYCLE_STATES.map((state) => (
                <MDBox key={state} sx={{ flex: 1, minWidth: 100, textAlign: "center", p: 1.5, borderRadius: "8px", border: "1px solid #E8EDF2", background: "#F8FAFC" }}>
                  <MDTypography variant="h4" fontWeight="bold" color="dark">{STATE_COUNTS[state] || 0}</MDTypography>
                  <StatusChip status={state} />
                </MDBox>
              ))}
            </MDBox>
            <MDBox mt={2}>
              <MDTypography variant="caption" color="secondary" sx={{ fontSize: 11 }}>
                Valid transitions: Provisioning → Active → Suspended/Maintenance/Offboarding | Suspended → Active/Offboarding | Maintenance → Active | Offboarding → Archived
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>

        <Grid container spacing={3}>
          {/* Provisioning Queue */}
          <Grid item xs={12} md={6}>
            <Card sx={{ border: "1px solid #E8EDF2", height: "100%" }}>
              <MDBox px={2.5} py={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold" color="dark">Provisioning Queue</MDTypography>
                <Chip label={`${PROVISIONING_QUEUE.length} active`} size="small" color="info" variant="outlined" />
              </MDBox>
              <Divider sx={{ m: 0 }} />
              <MDBox px={2.5} py={1.5}>
                {PROVISIONING_QUEUE.map((item, idx) => (
                  <MDBox key={idx} py={1.5} sx={{ borderBottom: idx < PROVISIONING_QUEUE.length - 1 ? "1px solid #F4F6F9" : "none" }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <MDBox>
                        <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: 13 }}>{item.tenant}</MDTypography>
                        <MDTypography variant="caption" color="secondary" display="block">{item.step}</MDTypography>
                      </MDBox>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#00ACC4" }}>{item.progress}/{item.total}</span>
                    </MDBox>
                    <MDBox sx={{ height: 4, background: "#ECF0F1", borderRadius: 2, overflow: "hidden" }}>
                      <MDBox sx={{ width: `${(item.progress / item.total) * 100}%`, height: "100%", background: "#00ACC4", borderRadius: 2 }} />
                    </MDBox>
                    <MDTypography variant="caption" color="secondary" sx={{ fontSize: 10, mt: 0.5, display: "block" }}>Started: {item.started}</MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </Card>
          </Grid>

          {/* Offboarding Tracker */}
          <Grid item xs={12} md={6}>
            <Card sx={{ border: "1px solid #E8EDF2", height: "100%" }}>
              <MDBox px={2.5} py={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold" color="dark">Offboarding Tracker</MDTypography>
                <Chip label={`${OFFBOARDING_TRACKER.length} in progress`} size="small" color="warning" variant="outlined" />
              </MDBox>
              <Divider sx={{ m: 0 }} />
              <MDBox px={2.5} py={1.5}>
                {OFFBOARDING_TRACKER.map((item, idx) => (
                  <MDBox key={idx} py={1.5}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: 13 }}>{item.tenant}</MDTypography>
                        <MDTypography variant="caption" color="secondary" display="block">{item.stage}</MDTypography>
                      </MDBox>
                      <MDBox textAlign="right">
                        <MDTypography variant="caption" fontWeight="bold" color="warning">{item.daysLeft} days left</MDTypography>
                        <MDTypography variant="caption" color="secondary" display="block" sx={{ fontSize: 10 }}>Initiated: {item.initiated}</MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                ))}
                {OFFBOARDING_TRACKER.length === 0 && (
                  <MDTypography variant="caption" color="secondary">No active offboarding</MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Recent State Transitions */}
          <Grid item xs={12}>
            <Card sx={{ border: "1px solid #E8EDF2" }}>
              <MDBox px={2.5} py={2}>
                <MDTypography variant="h6" fontWeight="bold" color="dark">Recent State Transitions</MDTypography>
              </MDBox>
              <Divider sx={{ m: 0 }} />
              <MDBox px={2.5} py={1}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #E8EDF2" }}>
                      {["Tenant", "Transition", "Reason", "Actor", "Time"].map((h) => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#5D6D7E", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {RECENT_TRANSITIONS.map((t, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #F4F6F9" }}>
                        <td style={{ padding: "10px 12px", fontWeight: 600, color: "#1E3A5F" }}>{t.tenant}</td>
                        <td style={{ padding: "10px 12px" }}>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            {t.from && <StatusChip status={t.from} />}
                            {t.from && <span style={{ color: "#BDC3C7", fontSize: 12 }}>→</span>}
                            <StatusChip status={t.to} />
                          </MDBox>
                        </td>
                        <td style={{ padding: "10px 12px", color: "#5D6D7E", fontSize: 12 }}>{t.reason}</td>
                        <td style={{ padding: "10px 12px", color: "#5D6D7E", fontSize: 12 }}>{t.actor}</td>
                        <td style={{ padding: "10px 12px", color: "#5D6D7E", fontSize: 11 }}>{t.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Lifecycle;
